import { cur } from '@/lib/currency';
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HandCoins, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import api from '@/lib/api';
import { CustodyActionDialog } from './CustodyActionDialog';

const STATUS_CLS = { pending: 'bg-amber-100 text-amber-800', received: 'bg-sky-100 text-sky-800', partially_received: 'bg-orange-100 text-orange-800', disputed: 'bg-red-100 text-red-700', settled: 'bg-emerald-100 text-emerald-800' };
const newRef = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

function HolderBalances({ holders }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="custody-holders">
      {holders.length === 0 && <div className="text-sm text-muted-foreground">Nobody is holding branch cash.</div>}
      {holders.map((h) => (
        <div key={`${h.owner_branch_id}-${h.holder_employee_id}`} className="p-3 rounded-xl border bg-white" data-testid={`custody-holder-${h.holder_employee_id}`}>
          <div className="font-semibold text-sm">{h.holder_name}</div>
          <div className="text-[11px] text-muted-foreground">Owned by {h.owner_branch_name} · oldest {h.oldest_days} day(s)</div>
          <div className="flex gap-4 mt-1 text-sm"><span>Holds <b>{cur()} {h.held_balance.toFixed(2)}</b></span>
            {h.in_transit > 0 && <span className="text-error">Unconfirmed {h.in_transit.toFixed(2)}</span>}</div>
        </div>
      ))}
    </div>
  );
}

function NewHandoverDialog({ open, onClose, onDone, branches, recipients }) {
  const [f, setF] = useState({ owner_branch_id: '', holder_employee_id: '', amount: '', notes: '' });
  const [ref, setRef] = useState(newRef());
  const save = async () => {
    try {
      await api.post('/cash-custody', { source: 'branch_cash', ...f, amount: parseFloat(f.amount), client_ref: ref });
      toast.success('Handover recorded — recipient must confirm'); setRef(newRef()); onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Not saved'); }
  };
  const sel = (k, opts, ph) => (
    <select className="w-full border rounded-md h-9 px-2 text-sm bg-background" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`new-handover-${k}`}>
      <option value="">{ph}</option>{opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-handover-dialog">
        <DialogHeader><DialogTitle className="font-outfit">Cash handover (custody)</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">The branch still owns this cash. No sale, expense, loan or salary advance is created.</p>
        <div className="space-y-3">
          {sel('owner_branch_id', branches, 'Owning branch (cash from branch safe)…')}
          {sel('holder_employee_id', recipients, 'Person who will hold it…')}
          <Input type="number" step="0.01" placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="new-handover-amount" />
          <Input placeholder="Purpose / note" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="new-handover-notes" />
          <Button className="rounded-full" onClick={save} data-testid="new-handover-submit">Hand over</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecordActions({ r, onAct }) {
  const b = (a, label) => <Button key={a} size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAct(a, r)} data-testid={`custody-${a}-btn-${r.id}`}>{label}</Button>;
  const out = [];
  if (r.can_confirm) out.push(b('confirm', 'Confirm'));
  if (r.can_operate && r.held_balance > 0) out.push(b('deposit', 'Deposit'), b('expense', 'Bill'), b('return', 'Return'), b('pass', 'Pass on'));
  if (r.can_operate && r.confirmed_at && r.in_transit > 0) out.push(b('resolve', 'Resolve'));
  if (r.can_cancel) out.push(b('cancel', 'Reverse'));
  return <div className="flex flex-wrap gap-1 justify-end">{out}</div>;
}

export function CashCustodyPanel({ branches, bankAccounts }) {
  const [enabled, setEnabled] = useState(null);
  const [rows, setRows] = useState([]);
  const [holders, setHolders] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [act, setAct] = useState({ action: null, record: null });

  const load = useCallback(async () => {
    const { data } = await api.get('/cash-custody/enabled');
    setEnabled(!!data?.enabled);
    if (!data?.enabled) return;
    const [r, h, p] = await Promise.all([api.get('/cash-custody'), api.get('/cash-custody/holders'), api.get('/cash-custody/recipients')]);
    setRows(r.data || []); setHolders(h.data || []); setRecipients(p.data || []);
  }, []);
  useEffect(() => { load().catch(() => setEnabled(false)); }, [load]);

  if (!enabled) return null;
  return (
    <Card className="border-amber-200" data-testid="cash-custody-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
        <CardTitle className="font-outfit flex items-center gap-2"><HandCoins size={18} /> Cash held by people (branch-owned)</CardTitle>
        <Button size="sm" className="rounded-full" onClick={() => setShowNew(true)} data-testid="new-handover-btn"><Plus size={14} className="mr-1" />Cash handover</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <HolderBalances holders={holders} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="custody-table">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="p-2">Date</th><th className="p-2">Owner branch</th><th className="p-2">Holder</th><th className="p-2 text-right">Given</th>
              <th className="p-2 text-right">Confirmed</th><th className="p-2 text-right">Holds</th><th className="p-2">Age</th><th className="p-2">Status</th><th className="p-2" />
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b" data-testid={`custody-row-${r.id}`}>
                  <td className="p-2">{format(new Date(r.created_at), 'MMM dd, yyyy')}</td>
                  <td className="p-2">{r.from_branch_name}</td>
                  <td className="p-2">{r.holder_name}<div className="text-[10px] text-muted-foreground">by {r.sender_name}{r.confirmed_on_behalf ? ` · confirmed by ${r.confirmed_by_name}` : ''}</div></td>
                  <td className="p-2 text-right">{Number(r.amount).toFixed(2)}</td>
                  <td className="p-2 text-right">{Number(r.received_amount || 0).toFixed(2)}</td>
                  <td className="p-2 text-right font-semibold" data-testid={`custody-held-${r.id}`}>{Number(r.held_balance || 0).toFixed(2)}</td>
                  <td className="p-2">{r.age_days ?? '-'}d</td>
                  <td className="p-2"><Badge className={`capitalize ${STATUS_CLS[r.status] || ''}`} data-testid={`custody-status-${r.id}`}>{String(r.status).replace('_', ' ')}</Badge></td>
                  <td className="p-2"><RecordActions r={r} onAct={(action, record) => setAct({ action, record })} /></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No cash handovers yet</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
      <NewHandoverDialog open={showNew} onClose={() => setShowNew(false)} onDone={load} branches={branches} recipients={recipients} />
      <CustodyActionDialog {...act} onClose={() => setAct({ action: null, record: null })} onDone={load}
        bankAccounts={bankAccounts} recipients={recipients} branches={branches} />
    </Card>
  );
}
